/**
 * Zeitplanung fuer den 3D-Start (Plan `2026-10-02-mat-3d-website`, Welle 3).
 * Ohne three.js: der Haupt-Chunk (Insel) und der 3D-Chunk benutzen es beide.
 *
 * Der 3D-Start kostete auf dem Mobile-Profil eine einzige Aufgabe von rund
 * 400 ms (TBT +351 ms in #934). Er beginnt deshalb erst in einer Leerlaufphase
 * nach `load` und gibt zwischen seinen Schritten den Hauptthread frei.
 */

/**
 * Laengste Wartezeit auf eine Leerlaufphase. Danach startet die Figur auch auf
 * einem Hauptthread, der nie frei wird — «nach Leerlauf» darf nicht «nie» heissen.
 */
export const MAT3D_IDLE_TIMEOUT_MS = 2000;

/** Ersatz fuer `requestIdleCallback` (fehlt in Safari): feste Pause nach `load`. */
export const MAT3D_IDLE_FALLBACK_MS = 500;

type IdleOptions = { timeout: number };
type IdleWindow = Window & {
    requestIdleCallback?: (callback: () => void, options?: IdleOptions) => number;
    cancelIdleCallback?: (handle: number) => void;
};
type SchedulerLike = { yield?: () => Promise<void> };

const nextTimer = (): Promise<void> =>
    new Promise(resolve => {
        setTimeout(resolve, 0);
    });

/**
 * Beendet die laufende Aufgabe und setzt danach fort: `scheduler.yield()` wo
 * vorhanden, sonst ein Timer.
 *
 * Mit `scheduler.yield()` laeuft immer auch ein Timer mit; wer zuerst kommt,
 * setzt fort. Chromium stellt die Fortsetzung von `scheduler.yield()` hinter
 * eine Flut von `MessageChannel`-Aufgaben (so plant React seine Arbeit) und
 * fuehrte sie in der Resilienz-Suite nie aus; Timer kamen dort weiter dran.
 */
export const yieldToMain = (): Promise<void> => {
    const scheduler = (globalThis as { scheduler?: SchedulerLike }).scheduler;
    if (typeof scheduler?.yield === "function") {
        return Promise.race([scheduler.yield(), nextTimer()]);
    }
    return nextTimer();
};

/**
 * Fuehrt `work` nach dem `load`-Ereignis in einer Leerlaufphase aus, spaetestens
 * nach `MAT3D_IDLE_TIMEOUT_MS`. Ohne `requestIdleCallback` nach
 * `MAT3D_IDLE_FALLBACK_MS`. Liefert eine Funktion, die den Auftrag zuruecknimmt.
 */
export const afterLoadWhenIdle = (work: () => void): (() => void) => {
    const w = window as IdleWindow;
    let cancelled = false;
    let idleHandle: number | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const run = (): void => {
        if (!cancelled) {
            work();
        }
    };
    const schedule = (): void => {
        if (cancelled) {
            return;
        }
        if (typeof w.requestIdleCallback === "function") {
            idleHandle = w.requestIdleCallback(run, { timeout: MAT3D_IDLE_TIMEOUT_MS });
        } else {
            timer = setTimeout(run, MAT3D_IDLE_FALLBACK_MS);
        }
    };

    if (document.readyState === "complete") {
        schedule();
    } else {
        window.addEventListener("load", schedule, { once: true });
    }

    return () => {
        cancelled = true;
        window.removeEventListener("load", schedule);
        if (idleHandle !== null) {
            w.cancelIdleCallback?.(idleHandle);
        }
        if (timer !== null) {
            clearTimeout(timer);
        }
    };
};
